// ===============================================================
// ProBank 15V — Download Page (/download)
//
// Public page (no login, no country guard).
//
//   - Opened inside the ProBank 15V app → "You already have it"
//   - Android in browser → big APK download button + guide
//   - iPhone / others → browser install steps as the main option
//
// Explains Android's security warnings (unknown sources, Samsung
// Auto Blocker) so users are not scared off.
// ===============================================================

import React, { useState } from "react";
import { Link } from "react-router-dom";
import InstallAppPrompt from "../components/InstallAppPrompt";

function insideNativeApp() {
  if (typeof window === "undefined") return false;
  return /ProBankApp/i.test(window.navigator.userAgent || "");
}

function isIPhone() {
  if (typeof window === "undefined") return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

export default function DownloadPage() {
  const [downloading, setDownloading] = useState(false);
  const inApp = insideNativeApp();
  const iPhone = isIPhone();

  const handleApkDownload = () => {
    setDownloading(true);
    const a = document.createElement("a");
    a.href = "/probank.apk";
    a.download = "probank.apk";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setDownloading(false), 3000);
  };

  const steps = [
    'Tap "Download APK" above. If your phone says the file cannot be downloaded securely, tap "Keep".',
    'When it finishes, open the file. If Android asks to allow installs from this source, tap Settings and allow it just this once.',
    'On Samsung phones, if "Auto Blocker" stops the install, open Settings → Security and privacy → Auto Blocker and turn it off for the install. You can turn it back on after.',
    'Tap "Install". ProBank 15V appears on your home screen like any other app.',
    'Open it and log in with your ProBank account, or register if you are new.',
  ];

  return (
    <div
      className="min-h-screen text-[#EDEAE3]"
      style={{
        background: "#0B0D10",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
      }}
    >
      {/* Header */}
      <header className="max-w-2xl mx-auto px-6 py-6 flex items-center justify-between border-b border-white/[0.08]">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#14171C]/80 border border-[#B8935F]/30 flex items-center justify-center">
            <img src="/banklogo.png" alt="ProBank 15V" className="w-6 h-6 opacity-90" />
          </div>
          <div className="text-base font-bold tracking-tight">ProBank 15V</div>
        </Link>
        <Link
          to="/login"
          className="text-sm text-[#D5D2C9] hover:text-[#EDEAE3] transition"
        >
          Log in
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 pt-14 pb-24">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-[#14171C] border border-[#B8935F]/30 flex items-center justify-center mx-auto mb-6">
            <img src="/icons/icon-192.png" alt="ProBank 15V app icon" className="w-14 h-14 rounded-xl" />
          </div>

          {inApp ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                You already have the app
              </h1>
              <p className="mt-4 text-[#D5D2C9] text-[15px] leading-relaxed max-w-md mx-auto">
                You are using the ProBank 15V app right now. There is nothing
                more to install — everything updates itself automatically.
              </p>
              <Link
                to="/dashboard"
                className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#B8935F] text-[#0B0D10] font-bold rounded-lg hover:bg-[#C7A472] transition text-base"
              >
                Go to my dashboard
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                Download the <span className="text-[#C7A472]">ProBank 15V</span> app
              </h1>
              <p className="mt-4 text-[#D5D2C9] text-[15px] leading-relaxed max-w-md mx-auto">
                Instant alerts when money moves, transfers, and your virtual card —
                straight from your home screen. Free, and no app store needed.
              </p>

              {!iPhone && (
                <>
                  <button
                    onClick={handleApkDownload}
                    disabled={downloading}
                    className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#B8935F] text-[#0B0D10] font-bold rounded-lg hover:bg-[#C7A472] transition text-base disabled:opacity-70"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {downloading ? "Downloading..." : "Download APK (Android)"}
                  </button>
                  <div className="mt-3 text-[12px] text-[#9B9DA3]">
                    Free · Works on Android phones
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* How to install — only shown to Android browser visitors */}
        {!inApp && !iPhone && (
          <div className="mt-14 bg-[#14171C] border border-white/[0.08] rounded-lg p-6 sm:p-8">
            <h2 className="font-semibold text-lg mb-5">How to install it</h2>
            <ol className="flex flex-col gap-4">
              {steps.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="w-7 h-7 rounded-full bg-[#B8935F]/[0.15] border border-[#B8935F]/30 flex items-center justify-center text-[#C7A472] font-bold text-[12px] shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[14px] text-[#D5D2C9] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>

            <div className="mt-6 p-4 rounded-lg bg-[#B8935F]/[0.08] border border-[#B8935F]/25">
              <div className="text-[13px] text-[#C7A472] font-semibold mb-1">
                About the Android warning
              </div>
              <p className="text-[13px] text-[#9B9DA3] leading-relaxed">
                Because this file comes from our website and not the Play Store,
                Android shows a standard caution message before installing. That
                is normal. The app is the same ProBank 15V you use on this website.
              </p>
            </div>
          </div>
        )}

        {/* Alternative: browser install (main option on iPhone) */}
        {!inApp && (
          <div className="mt-8">
            <div className="text-center text-[13px] text-[#9B9DA3] mb-4">
              {iPhone
                ? "On iPhone, install ProBank 15V straight from your browser:"
                : "Prefer not to download a file? Install it straight from your browser instead."}
            </div>
            <InstallAppPrompt />
          </div>
        )}

        {/* Footer note */}
        <div className="mt-12 text-center text-[12px] text-[#9B9DA3]">
          Already have an account?{" "}
          <Link to="/login" className="text-[#C7A472] hover:underline">
            Log in
          </Link>{" "}
          ·{" "}
          <Link to="/register" className="text-[#C7A472] hover:underline">
            Open an account
          </Link>
        </div>
      </main>
    </div>
  );
}
